// Abmelden nach 5 Minuten ohne Nutzung, Warnung 1 Minute vorher.
// Zählt Maus, Tastatur, Scrollen und Touch in allen Tabs (gemeinsame Zeit in
// localStorage). Während die Warnung läuft, verlängert nur der Knopf
// „Angemeldet bleiben“ – bloßes Bewegen der Maus reicht dann nicht.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  leseZeit, restSekunden, SCHLUESSEL_ABMELDUNG, SCHLUESSEL_AKTIVITAET,
  speicherLesen, speicherSchreiben, zustandBei,
} from '../utils/inaktivitaet';

const EREIGNISSE = ['mousemove', 'mousedown', 'keydown', 'scroll', 'wheel', 'touchstart'];
const SCHREIB_ABSTAND_MS = 5000;

export function useInaktivitaetsAbmeldung(angemeldet: boolean, abmelden: () => void) {
  const [rest, setRest] = useState<number | null>(null); // null = keine Warnung
  const abmeldenRef = useRef(abmelden);
  abmeldenRef.current = abmelden;
  const letzte = useRef(Date.now());
  const geschrieben = useRef(0);
  const warnt = useRef(false);

  const markieren = useCallback((sofort = false) => {
    const jetzt = Date.now();
    letzte.current = jetzt;
    if (sofort || jetzt - geschrieben.current >= SCHREIB_ABSTAND_MS) {
      speicherSchreiben(SCHLUESSEL_AKTIVITAET, String(jetzt));
      geschrieben.current = jetzt;
    }
  }, []);

  useEffect(() => {
    if (!angemeldet) {
      warnt.current = false;
      setRest(null);
      return undefined;
    }
    let beendet = false;
    // Gespeicherte Zeit zählt: Wird der Browser nach einer Pause wieder
    // geöffnet, meldet die erste Prüfung sofort ab.
    letzte.current = leseZeit(speicherLesen(SCHLUESSEL_AKTIVITAET)) ?? Date.now();

    const pruefen = () => {
      if (beendet) return;
      const andererTab = leseZeit(speicherLesen(SCHLUESSEL_AKTIVITAET)) ?? 0;
      const basis = Math.max(letzte.current, andererTab);
      letzte.current = basis;
      const jetzt = Date.now();
      const zustand = zustandBei(basis, jetzt);
      if (zustand === 'abmelden') {
        beendet = true;
        speicherSchreiben(SCHLUESSEL_ABMELDUNG, String(jetzt)); // andere Tabs folgen
        abmeldenRef.current();
        return;
      }
      warnt.current = zustand === 'warnung';
      setRest(warnt.current ? restSekunden(basis, jetzt) : null);
    };

    const nutzung = () => { if (!warnt.current) markieren(); };
    const sichtbar = () => { if (document.visibilityState === 'visible') pruefen(); };
    const speicher = (e: StorageEvent) => {
      if (e.key === SCHLUESSEL_ABMELDUNG && e.newValue && !beendet) {
        beendet = true;
        abmeldenRef.current();
      }
    };

    EREIGNISSE.forEach((ev) => document.addEventListener(ev, nutzung, { capture: true, passive: true }));
    document.addEventListener('visibilitychange', sichtbar);
    window.addEventListener('focus', pruefen);
    window.addEventListener('storage', speicher);
    const takt = window.setInterval(pruefen, 1000);
    pruefen();

    return () => {
      beendet = true;
      EREIGNISSE.forEach((ev) => document.removeEventListener(ev, nutzung, { capture: true }));
      document.removeEventListener('visibilitychange', sichtbar);
      window.removeEventListener('focus', pruefen);
      window.removeEventListener('storage', speicher);
      window.clearInterval(takt);
    };
  }, [angemeldet, markieren]);

  const weiter = useCallback(() => {
    warnt.current = false;
    markieren(true);
    setRest(null);
  }, [markieren]);

  return { rest, weiter };
}
